/** « Démarrage de l'agence » (A1, P6-01, D-148): the owner's step-by-step wizard. Every step reuses
 * an existing screen's API (Settings → Agency and Brand, the DocumentLayout preview, the catalogue,
 * Integrations, the AI sandbox, the public site); the wizard only records the progress, which is
 * saved on the server: the owner can stop and resume, skip a step (listed « à compléter ») and see
 * the final checklist with the total time. */

import {
  type OnboardingState,
  getOnboardingProgressCurrentRetrieveQueryKey,
  useOnboardingProgressCurrentPartialUpdate,
  useOnboardingProgressCurrentRetrieve,
  useOnboardingProgressFinishCreate,
  useOnboardingProgressStepCreate,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, CircleDashed, Timer } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../../components/AppShell";
import { formatDuration } from "./format";
import { AiStep, BrandStep, ConnectionsStep, ContractsStep, IdentityStep, ServicesStep, WebsiteStep } from "./steps";
import "./onboarding.css";

type StepAction = "complete" | "skip" | "reopen";
const CHECKLIST = "checklist";

function useElapsed(state: OnboardingState | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  const running = Boolean(state?.started_at && !state.completed_at);
  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [running]);
  if (!state?.started_at) return null;
  if (!running) return state.elapsed_seconds ?? null;
  return Math.max(0, Math.floor((now - new Date(state.started_at).getTime()) / 1000));
}

export function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone = status === "done" ? "success" : status === "skipped" ? "warning" : "default";
  return (
    <Badge tone={tone} dot>
      {t(`onboarding.status.${status}`)}
    </Badge>
  );
}

function StepIcon({ status }: { status: string }) {
  if (status === "done") return <CheckCircle2 size={18} strokeWidth={1.9} aria-hidden="true" />;
  if (status === "skipped") return <CircleDashed size={18} strokeWidth={1.9} aria-hidden="true" />;
  return <Circle size={18} strokeWidth={1.9} aria-hidden="true" />;
}

function Checklist({ state, onOpen }: { state: OnboardingState; onOpen: (key: string) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const finish = useOnboardingProgressFinishCreate({
    mutation: { onSuccess: (response) => queryClient.setQueryData(getOnboardingProgressCurrentRetrieveQueryKey(), response) },
  });
  const pending = state.steps.filter((s) => s.to_complete);
  return (
    <GlassCard className="stack onboarding__panel" aria-labelledby="onboarding-checklist">
      <h2 id="onboarding-checklist">{t("onboarding.checklist.title")}</h2>
      {state.completed_at && state.elapsed_seconds !== null ? (
        <p className="ok">{t("onboarding.checklist.finished", { time: formatDuration(state.elapsed_seconds) })}</p>
      ) : null}
      {pending.length === 0 ? <p className="ok">{t("onboarding.checklist.allDone")}</p> : null}
      <ul className="onboarding__checklist">
        {state.steps.map((step) => (
          <li key={step.key} className={`onboarding__check onboarding__check--${step.status}`}>
            <StepIcon status={step.status} />
            <div className="onboarding__check-body">
              <strong>{t(`onboarding.steps.${step.key}`)}</strong>
              {step.to_complete ? (
                <span className="muted">
                  {t("onboarding.checklist.toComplete")}
                  {step.missing.length ? ` : ${step.missing.map((m) => t(`onboarding.missing.${m}`)).join(", ")}` : ""}
                </span>
              ) : null}
            </div>
            <StatusBadge status={step.status} />
            <Button variant="plain" size="sm" onClick={() => onOpen(step.key)}>
              {t("onboarding.actions.open")}
            </Button>
          </li>
        ))}
      </ul>
      {!state.completed_at ? (
        <Button onClick={() => finish.mutate()} loading={finish.isPending}>
          {t("onboarding.actions.finish")}
        </Button>
      ) : null}
    </GlassCard>
  );
}

export function OnboardingPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const current = useOnboardingProgressCurrentRetrieve({ query: { staleTime: 0, retry: false } });
  const state = current.data?.status === 200 ? current.data.data : undefined;
  const [view, setView] = useState<string | null>(null);
  const keep = (response: { status: number; data: unknown }) => {
    if (response.status === 200) queryClient.setQueryData(getOnboardingProgressCurrentRetrieveQueryKey(), response);
  };
  const stepAction = useOnboardingProgressStepCreate({ mutation: { onSuccess: keep } });
  const resume = useOnboardingProgressCurrentPartialUpdate({ mutation: { onSuccess: keep } });
  const elapsed = useElapsed(state);

  if (current.isError) {
    return (
      <Page title={t("onboarding.title")}>
        <p role="alert" className="ko">
          {t("common.error")}
        </p>
      </Page>
    );
  }
  if (!state) {
    return (
      <Page title={t("onboarding.title")}>
        <Skeleton style={{ height: 320 }} />
      </Page>
    );
  }

  const keys = state.steps.map((s) => s.key);
  const active = view ?? state.current_step;
  const index = keys.indexOf(active);
  const step = state.steps.find((s) => s.key === active);
  const done = state.steps.filter((s) => s.status === "done").length;

  const open = (key: string) => {
    setView(key);
    if (key !== CHECKLIST) resume.mutate({ data: { current_step: key } });
  };
  const act = (action: StepAction) => {
    if (!step) return;
    stepAction.mutate(
      { data: { step: step.key, action } },
      {
        onSuccess: () => {
          if (action === "reopen") return;
          const next = keys[index + 1];
          setView(next ?? CHECKLIST);
        },
      },
    );
  };
  const onStepSaved = () => act("complete");

  return (
    <Page
      title={t("onboarding.title")}
      description={t("onboarding.intro")}
      actions={
        <div className="onboarding__timer" role="timer" aria-live="off">
          <Timer size={16} strokeWidth={1.9} aria-hidden="true" />
          <span>
            {state.completed_at
              ? t("onboarding.timerTotal", { time: formatDuration(elapsed ?? 0) })
              : t("onboarding.timer", { time: formatDuration(elapsed ?? 0) })}
          </span>
          <small className="muted">{t("onboarding.timerTarget", { minutes: state.target_minutes })}</small>
        </div>
      }
    >
      <div className="onboarding">
        <nav className="onboarding__steps ao-glass" aria-label={t("onboarding.stepsLabel")}>
          <p className="muted">{t("onboarding.progress", { done, total: keys.length })}</p>
          <ol>
            {state.steps.map((s, i) => (
              <li key={s.key}>
                <button
                  type="button"
                  className={`onboarding__step onboarding__step--${s.status}${s.key === active ? " is-active" : ""}`}
                  aria-current={s.key === active ? "step" : undefined}
                  onClick={() => open(s.key)}
                >
                  <StepIcon status={s.status} />
                  <span>
                    {i + 1}. {t(`onboarding.steps.${s.key}`)}
                  </span>
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                className={`onboarding__step${active === CHECKLIST ? " is-active" : ""}`}
                aria-current={active === CHECKLIST ? "step" : undefined}
                onClick={() => open(CHECKLIST)}
              >
                <CheckCircle2 size={18} strokeWidth={1.9} aria-hidden="true" />
                <span>{t("onboarding.checklist.title")}</span>
              </button>
            </li>
          </ol>
        </nav>

        {active === CHECKLIST || !step ? (
          <Checklist state={state} onOpen={open} />
        ) : (
          <GlassCard className="stack onboarding__panel" aria-labelledby="onboarding-step-title">
            <div className="row between">
              <h2 id="onboarding-step-title">
                {index + 1}. {t(`onboarding.steps.${step.key}`)}
              </h2>
              <StatusBadge status={step.status} />
            </div>
            <p className="muted">{t(`onboarding.hints.${step.key}`)}</p>
            {step.key === "identity" ? <IdentityStep onSaved={onStepSaved} /> : null}
            {step.key === "brand" ? <BrandStep /> : null}
            {step.key === "services" ? <ServicesStep state={state} /> : null}
            {step.key === "contracts" ? <ContractsStep /> : null}
            {step.key === "connections" ? <ConnectionsStep state={state} /> : null}
            {step.key === "ai" ? <AiStep state={state} /> : null}
            {step.key === "website" ? <WebsiteStep state={state} /> : null}
            {step.missing.length ? (
              <p className="onboarding__missing">
                {t("onboarding.checklist.toComplete")} : {step.missing.map((m) => t(`onboarding.missing.${m}`)).join(", ")}
              </p>
            ) : null}
            <div className="onboarding__actions">
              <Button variant="plain" disabled={index <= 0} onClick={() => open(keys[index - 1] ?? keys[0] ?? "")}>
                {t("onboarding.actions.back")}
              </Button>
              {step.status !== "todo" ? (
                <Button variant="plain" onClick={() => act("reopen")}>
                  {t("onboarding.actions.reopen")}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => act("skip")} disabled={stepAction.isPending}>
                {t("onboarding.actions.skip")}
              </Button>
              <Button onClick={() => act("complete")} loading={stepAction.isPending}>
                {t("onboarding.actions.complete")}
              </Button>
            </div>
          </GlassCard>
        )}
      </div>
    </Page>
  );
}
